import {
  HdBodygraphCenter,
  HdBodygraphConfig,
  HdBodygraphPlanetaryActivation,
  HdBodygraphRenderState,
} from '../../bodygraph/hd-bodygraph.models';
import { HD_BODYGRAPH_CHANNELS } from '../../bodygraph/hd-bodygraph-metadata';
import { hdGatesCountingForChannel, hdPreShiftGates } from '../../hd-global-shift';
import {
  getHdChannelManifestationType,
  HdChannelManifestationType,
} from '../../bodygraph/hd-channel-manifestation-type';
import { getDefinedClusters } from '../../bodygraph/basic-properties/hd-chart-properties.util';
import { HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON } from '../../bodygraph/planet-grid/hd-planet-display-config';

export const HD_JUST_NOW_TRANSIT_COLOR = '#6bcdae';
export const HD_JUST_NOW_TRANSIT_TEXT_COLOR = '#347d69';
export const HD_JUST_NOW_TRANSIT_BASE_COLOR = '#285a82';

export type HdJustNowTransitColorMode = 'two-color' | 'three-color';

export interface HdJustNowTransitFormation {
  readonly channelKey: string;
  readonly gates: readonly [number, number];
  readonly anchorGate: number;
  readonly transitGates: readonly number[];
  readonly baseActivations: readonly HdBodygraphPlanetaryActivation[];
  readonly transitActivations: readonly HdBodygraphPlanetaryActivation[];
  readonly manifestationType: HdChannelManifestationType;
  readonly newlyDefinedCenters: readonly HdBodygraphCenter[];
  readonly bridgesSplit: boolean;
}

export interface HdJustNowTransitOverview {
  readonly transitActivations: readonly HdBodygraphPlanetaryActivation[];
  readonly transitGates: ReadonlySet<number>;
  readonly formations: readonly HdJustNowTransitFormation[];
  readonly formationsByGate: ReadonlyMap<number, readonly HdJustNowTransitFormation[]>;
  readonly formationsByChannelKey: ReadonlyMap<string, HdJustNowTransitFormation>;
}

export interface HdJustNowTransitComputationInput {
  readonly baseConfiguration: HdBodygraphConfig;
  readonly basePlanetaryActivations: readonly HdBodygraphPlanetaryActivation[];
  readonly transitPersonalityActivations: readonly HdBodygraphPlanetaryActivation[];
  readonly baseRenderState: HdBodygraphRenderState;
}

const PLANET_ORDER = new Map(
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON.map((planet, index) => [planet.planetId, index]),
);

export function filterHdJustNowPersonalityActivations(
  activations: readonly HdBodygraphPlanetaryActivation[],
): HdBodygraphPlanetaryActivation[] {
  const seenIds = new Set<string>();
  return activations.filter(activation => {
    if (activation.side !== 'personality') {
      return false;
    }
    if (activation.sourceType !== 'planet' || activation.id.trim().length === 0) {
      return false;
    }
    if (!Number.isInteger(activation.gate) || activation.gate < 1 || activation.gate > 64) {
      return false;
    }
    if (!Number.isInteger(activation.planetId) || activation.planetId < 0) {
      return false;
    }
    if (!Number.isInteger(activation.line) || activation.line < 1 || activation.line > 6) {
      return false;
    }
    if (seenIds.has(activation.id)) {
      return false;
    }
    seenIds.add(activation.id);
    return true;
  });
}

export function buildHdJustNowTransitOverview(
  input: HdJustNowTransitComputationInput,
): HdJustNowTransitOverview {
  const transitActivations = filterHdJustNowPersonalityActivations(input.transitPersonalityActivations);
  const baseActiveGates = new Set(input.baseConfiguration.activations.map(activation => activation.gate));
  const transitGates = new Set(transitActivations.map(activation => activation.gate));
  const clusterIndexByCenter = buildClusterIndex(input.baseRenderState);
  const formations: HdJustNowTransitFormation[] = [];
  const formationsByGate = new Map<number, HdJustNowTransitFormation[]>();
  const formationsByChannelKey = new Map<string, HdJustNowTransitFormation>();

  // Kanaalbreuk na de shift (19/49): poorten van een post-2027-geboorte tellen
  // niet mee voor dat kanaal; transitpoorten wel (zie hd-global-shift.ts).
  const preShiftBaseGates = hdPreShiftGates(input.baseConfiguration.activations);

  for (const channel of HD_BODYGRAPH_CHANNELS) {
    const [gateA, gateB] = channel.gates;
    const countingBaseGates = hdGatesCountingForChannel(channel.key, baseActiveGates, preShiftBaseGates);
    if (countingBaseGates.has(gateA) && countingBaseGates.has(gateB)) {
      continue;
    }
    const isCombinedActive = (gate: number) => countingBaseGates.has(gate) || transitGates.has(gate);
    if (!isCombinedActive(gateA) || !isCombinedActive(gateB)) {
      continue;
    }

    const channelTransitGates = channel.gates.filter(gate => transitGates.has(gate));
    if (channelTransitGates.length === 0) {
      continue;
    }

    const channelGates = new Set(channel.gates);
    const formation: HdJustNowTransitFormation = {
      channelKey: channel.key,
      gates: channel.gates,
      anchorGate: resolveAnchorGate(channel.gates, baseActiveGates, transitActivations),
      transitGates: channelTransitGates,
      baseActivations: input.basePlanetaryActivations.filter(activation => channelGates.has(activation.gate)),
      transitActivations: transitActivations.filter(activation => channelGates.has(activation.gate)),
      manifestationType: getHdChannelManifestationType(channel.key) ?? 'projected',
      newlyDefinedCenters: channel.centers.filter(center => !input.baseRenderState.centerStates[center].isDefined),
      bridgesSplit: bridgesDefinedClusters(channel.centers, clusterIndexByCenter),
    };

    formations.push(formation);
    formationsByChannelKey.set(formation.channelKey, formation);
    for (const gate of channelTransitGates) {
      const gateFormations = formationsByGate.get(gate);
      if (gateFormations) {
        gateFormations.push(formation);
      } else {
        formationsByGate.set(gate, [formation]);
      }
    }
  }

  return {
    transitActivations,
    transitGates,
    formations,
    formationsByGate,
    formationsByChannelKey,
  };
}

function resolveAnchorGate(
  channelGates: readonly [number, number],
  baseActiveGates: ReadonlySet<number>,
  transitActivations: readonly HdBodygraphPlanetaryActivation[],
): number {
  const missingBaseGates = channelGates.filter(gate => !baseActiveGates.has(gate));
  const candidates = missingBaseGates.length > 0
    ? missingBaseGates
    : channelGates.filter(gate => transitActivations.some(activation => activation.gate === gate));

  return [...candidates].sort((left, right) => {
    const orderDifference = firstPlanetOrder(left, transitActivations) - firstPlanetOrder(right, transitActivations);
    if (orderDifference !== 0) {
      return orderDifference;
    }
    return channelGates.indexOf(left) - channelGates.indexOf(right);
  })[0] ?? channelGates[0];
}

function firstPlanetOrder(
  gate: number,
  activations: readonly HdBodygraphPlanetaryActivation[],
): number {
  return activations
    .filter(activation => activation.gate === gate)
    .reduce((lowest, activation) => Math.min(lowest, PLANET_ORDER.get(activation.planetId) ?? 999), 999);
}

function buildClusterIndex(renderState: HdBodygraphRenderState): ReadonlyMap<HdBodygraphCenter, number> {
  const clusterIndexByCenter = new Map<HdBodygraphCenter, number>();
  getDefinedClusters(renderState).forEach((cluster, clusterIndex) => {
    cluster.forEach(center => clusterIndexByCenter.set(center, clusterIndex));
  });
  return clusterIndexByCenter;
}

function bridgesDefinedClusters(
  centers: readonly [HdBodygraphCenter, HdBodygraphCenter],
  clusterIndexByCenter: ReadonlyMap<HdBodygraphCenter, number>,
): boolean {
  const [centerA, centerB] = centers;
  const clusterA = clusterIndexByCenter.get(centerA);
  const clusterB = clusterIndexByCenter.get(centerB);
  return clusterA != null && clusterB != null && clusterA !== clusterB;
}
